// @flow
import PatientDropdownLabel from 'components/PatientDropdownLabel'
import useSearchByName from 'hooks/useSearchByName'
import isNil from 'lodash/isNil'
import reject from 'lodash/reject'
import * as React from 'react'
import Ui from 'ui'
import Flex from 'ui/atoms/Flex'
import FlexBox from 'ui/atoms/FlexBox'
import { ZentakeContext } from 'utils/ZentakeContext'

type Patient = {
  id?: string,
  first_name?: string,
  last_name?: string,
  date_birth?: string,
  email?: string,
}

type Props = {
  isNewPatient: boolean,
  patient: ?Patient,
  setFirstName: (value: string) => void,
  setLastName: (value: string) => void,
  formError: {
    first_name: boolean,
    last_name: boolean,
  },
  formErrorLabel: {
    first_name?: ?string,
    last_name?: ?string,
  },
  onPersonSelected: (person: Patient) => void,
  onClearSelectedPerson: () => void,
}

function TabletModePatientFieldsWithSearch({
  isNewPatient,
  patient,
  setFirstName,
  setLastName,
  formError,
  formErrorLabel,
  onPersonSelected,
  onClearSelectedPerson,
}: Props): React.Node {
  const { clients, fetchingClients, teamConfig } =
    React.useContext(ZentakeContext)

  const clientLabel = teamConfig?.client_label || 'Client'

  const { handleFirstNameType, handleLastNameType } = useSearchByName()

  const [clientsForSelector, setClientsForSelector] = React.useState(clients)

  React.useEffect(() => {
    setClientsForSelector(
      reject(clients, (client) =>
        isNil(client?.email ?? client?.phone_number ?? client?.date_birth)
      )
    )
  }, [clients])

  return (
    <>
      <Flex $horizontalSpacing="medium">
        <FlexBox>
          <Flex justify="between">
            <Ui.QuestionTitle>{clientLabel} First Name</Ui.QuestionTitle>
            {!isNewPatient && (
              <Ui.ButtonClear
                className="text-zentake"
                onClick={onClearSelectedPerson}
                size="2xs"
              >
                Clear
              </Ui.ButtonClear>
            )}
          </Flex>
          <Ui.Select
            LabelRenderer={({ data }) => (
              <PatientDropdownLabel patient={data} />
            )}
            disabled={!isNewPatient}
            errorLabel={formErrorLabel?.first_name}
            hasError={formError?.first_name}
            loading={fetchingClients}
            onChange={onPersonSelected}
            onCreate={setFirstName}
            onType={handleFirstNameType}
            options={clientsForSelector || []}
            placeholder="First Name"
            selectedLabelFormatter={(x) => x.first_name}
            target="first_name"
            value={patient}
            valueProp="id"
          />
        </FlexBox>
        <FlexBox>
          <Ui.Select
            LabelRenderer={({ data }) => (
              <PatientDropdownLabel patient={data} />
            )}
            disabled={!isNewPatient}
            errorLabel={formErrorLabel?.last_name}
            hasError={formError?.last_name}
            label={`${clientLabel} Last Name`}
            loading={fetchingClients}
            onChange={onPersonSelected}
            onCreate={setLastName}
            onType={handleLastNameType}
            options={clientsForSelector || []}
            placeholder={`Last Name`}
            selectedLabelFormatter={(x) => x.last_name}
            target="last_name"
            value={patient}
            valueProp="id"
          />
        </FlexBox>
      </Flex>
    </>
  )
}

export default TabletModePatientFieldsWithSearch
